import { useState, useEffect } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  Input,
  RadixSelect,
  SectionCard,
  FormSection,
  Switch,
} from '@/components/ui';
import type {
  AdminCategoryView,
  CategoryCreatePayload,
  CategoryDisplayType,
  CategoryUpdatePayload,
} from '../hooks/useAdminCategoriesQuery';

/**
 * C-1 (ID103) 전시 유형 옵션 — SF 서브탭(EVER MUSIC/MD/CON/FRIENDS/LIGHT STICK/예약판매) 매핑 기준.
 * 첫 옵션('')은 미분류(서브탭 무소속). 라벨은 한국어(백오피스 전용). BE CategoryDisplayType 과 1:1.
 */
const DISPLAY_TYPE_OPTIONS: ReadonlyArray<{ value: '' | CategoryDisplayType; label: string }> = [
  { value: '', label: '미분류' },
  { value: 'MUSIC', label: 'EVER MUSIC' },
  { value: 'MD', label: 'EVER MD' },
  { value: 'CON', label: 'EVER CON' },
  { value: 'FRIENDS', label: 'EVER FRIENDS' },
  { value: 'LIGHT_STICK', label: 'LIGHT STICK' },
  { value: 'PRE_ORDER', label: '예약판매(PRE-ORDER)' },
];

interface CategoryFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  parentCategory?: AdminCategoryView | null;
  editTarget?: AdminCategoryView | null;
  isPending: boolean;
  onSubmit: (payload: CategoryCreatePayload | CategoryUpdatePayload) => void;
}

export const CategoryFormModal = ({
  open,
  onOpenChange,
  mode,
  parentCategory,
  editTarget,
  isPending,
  onSubmit,
}: CategoryFormModalProps) => {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [categoryCode, setCategoryCode] = useState('');
  const [displayType, setDisplayType] = useState<'' | CategoryDisplayType>('');
  // 이슈1 — B2B 전용 노출 토글. 켜짐 = B2B(기업) 회원 전용.
  const [b2bOnly, setB2bOnly] = useState(false);
  const [sortOrder, setSortOrder] = useState('0');
  // 노출(active) — 끄면 숨김(고객 화면 비노출). 수정 화면에서만 바꾼다(새 카테고리는 BE 기본 노출).
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && editTarget) {
        setName(editTarget.name);
        setCategoryCode(editTarget.categoryCode);
        setDisplayType(editTarget.displayType ?? '');
        setB2bOnly(editTarget.b2bOnly ?? false);
        setSortOrder(String(editTarget.sortOrder));
        setActive(editTarget.active);
      } else {
        setName('');
        setCategoryCode('');
        setDisplayType('');
        setB2bOnly(false);
        setSortOrder('0');
        setActive(true);
      }
    }
  }, [open, mode, editTarget]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !categoryCode.trim()) return;

    // 미분류('')는 null 로 전송(유형 해제).
    const typeValue: CategoryDisplayType | null = displayType === '' ? null : displayType;

    if (mode === 'create') {
      onSubmit({
        name: name.trim(),
        categoryCode: categoryCode.trim(),
        displayType: typeValue,
        b2bOnly,
        parentCategoryId: parentCategory?.categoryId ?? null,
        sortOrder: Number(sortOrder) || 0,
      } satisfies CategoryCreatePayload);
    } else {
      onSubmit({
        name: name.trim(),
        categoryCode: categoryCode.trim(),
        displayType: typeValue,
        b2bOnly,
        sortOrder: Number(sortOrder) || 0,
        active,
      } satisfies CategoryUpdatePayload);
    }
  };

  const titleText =
    mode === 'create'
      ? parentCategory
        ? t('cms.categories.createChild', { parent: parentCategory.name })
        : t('cms.categories.createRoot')
      : t('cms.categories.editTitle');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titleText}</DialogTitle>
          {mode === 'create' && parentCategory && (
            <DialogDescription>
              {t('cms.categories.createChildDesc', { parent: parentCategory.name, depth: parentCategory.depth + 1 })}
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <SectionCard>
            <div className="flex flex-col gap-3">
              <FormSection density="compact" label={t('cms.categories.fields.name')} required>
                <Input
                  id="cat-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                  style={{ height: 'var(--density-compact-form-h)' }}
                />
              </FormSection>
              <FormSection density="compact" label={t('cms.categories.fields.code')} required>
                <Input
                  id="cat-code"
                  value={categoryCode}
                  onChange={(e) => setCategoryCode(e.target.value)}
                  required
                  className="font-mono"
                  style={{ height: 'var(--density-compact-form-h)' }}
                />
              </FormSection>
              {/* C-1(ID103) 전시 유형 — SF 서브탭 렌더 기준. 미분류 가능(선택). */}
              <FormSection
                density="compact"
                label={t('cms.categories.fields.displayType', { defaultValue: '전시 유형' })}
              >
                <RadixSelect
                  id="cat-display-type"
                  value={displayType}
                  onChange={(v) => setDisplayType(v as '' | CategoryDisplayType)}
                  options={DISPLAY_TYPE_OPTIONS}
                />
              </FormSection>
              {/* 이슈1 — B2B 전용 노출. 켜면 이 카테고리(+하위·소속 상품)는 B2B(기업) 회원에게만
                  스토어프론트에 노출되고, 일반/비회원에게는 숨겨진다(서버 필터 + SF 분기). */}
              <FormSection
                density="compact"
                label={t('cms.categories.fields.b2bOnly', { defaultValue: 'B2B 전용 노출' })}
                hint={t('cms.categories.fields.b2bOnlyHint', {
                  defaultValue: '켜면 B2B(기업) 회원에게만 노출됩니다. 일반·비회원에게는 숨겨집니다.',
                })}
              >
                <Switch
                  id="cat-b2b-only"
                  checked={b2bOnly}
                  onCheckedChange={setB2bOnly}
                  aria-label={t('cms.categories.fields.b2bOnly', { defaultValue: 'B2B 전용 노출' })}
                />
              </FormSection>
              {mode === 'edit' ? (
                <FormSection
                  density="compact"
                  label={t('cms.categories.fields.active', { defaultValue: '고객 화면 노출' })}
                  hint={t('cms.categories.fields.activeHint', {
                    defaultValue: '끄면 숨김 카테고리가 됩니다. 고객 화면에 보이지 않지만 삭제되지 않고, 이 목록에는 "숨김"으로 남습니다.',
                  })}
                >
                  <Switch
                    id="cat-active"
                    checked={active}
                    onCheckedChange={setActive}
                    aria-label={t('cms.categories.fields.active', { defaultValue: '고객 화면 노출' })}
                    data-testid="category-active-switch"
                  />
                </FormSection>
              ) : null}
              <FormSection density="compact" label={t('cms.categories.fields.sortOrder')}>
                <Input
                  id="cat-order"
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  min={0}
                  style={{ height: 'var(--density-compact-form-h)' }}
                />
              </FormSection>
            </div>
          </SectionCard>
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button type="submit" size="sm" disabled={isPending || !name.trim() || !categoryCode.trim()}>
              {t('cms.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
